/**
 * EFormAccordion — read-only subset
 * Ported from pitstop-ui/src/components/EFormV2/EFormAccordion.jsx
 *
 * Stripped: EFormCreateBtn, DeleteIcon SVG imports (don't exist in submittals-ui
 *   and are never rendered since isReadOnly is always true). renderCreateAccordion,
 *   handleCreateAccordion, handleDeleteAccordion — all guarded by !isReadOnly
 *   so they are dead code for this app.
 * Kept: expand/collapse toggle, title/subtitle, thread connector, isRequired asterisk.
 */
import { useState } from 'react'
import ArrowDown from '@/assets/svgs/chevron-down.svg?react'
import PropTypes from 'prop-types'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import InfoIcon from '@/assets/svgs/information-circle.svg?react'

const parseDescription = description => {
  if (!description) return null
  if (!description.startsWith('<root>')) return description
  const match = description.match(/<label>description<\/label>:<b>(.*?)<\/b>/)
  return match?.[1] ?? null
}

const HEADER_BG = {
  0: 'bg-transparent',
  1: 'bg-white',
  2: 'bg-grey-98',
  3: 'bg-grey-95',
  4: 'bg-grey-90',
  5: 'bg-grey-70',
}

const EFormAccordion = ({
  testId,
  title,
  subTitle,
  defaultState = true,
  children,
  level = 0,
  fieldType,
  isRequired,
  arrIdx = 0,
  isCreated = false,
  description,
}) => {
  const [isOpen, setIsOpen] = useState(defaultState)

  const desc = parseDescription(description)
  const tooltipId = desc ? `accordion-desc-${testId}` : null

  // In read-only mode optional items that have no data are not shown
  if (!isCreated && !isRequired) return null

  return (
    <div>
      <div className="flex flex-row" data-testid={`${testId}__accordion`}>
        {level !== 1 && <span className="thread" />}
        <div
          className={`${HEADER_BG[level] ?? 'bg-transparent'} flex items-center w-[98%] min-h-nav-sticky [@media(max-height:600px)]:min-h-[10vh] my-2.5 py-2.5 px-4.5 cursor-pointer rounded-3 gap-2.5`}
        >
          <div
            className="flex items-center w-full gap-2.5 cursor-pointer"
            onClick={() => setIsOpen(o => !o)}
          >
            <div
              className="flex items-center justify-center [&_svg_rect]:fill-theme-50"
              style={{
                transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            >
              <ArrowDown />
            </div>
            <div className="flex">
              {isRequired && (
                <span
                  className="h-4 mr-1 font-black text-red-50"
                  data-testid="eform-label-asterisk"
                >
                  *
                </span>
              )}
              <div>
                <div className="type-header-4 text-grey-10">
                  {title}
                  {fieldType === 'array' && ` #${arrIdx + 1}`}
                  {tooltipId && (
                    <span
                      className="inline-flex items-center ml-1 cursor-default align-middle [&_svg]:w-[18px] [&_svg]:h-[18px] [&_svg]:text-grey-50 hover:[&_svg]:text-grey-10"
                      data-tooltip-id={tooltipId}
                      onClick={e => e.stopPropagation()}
                    >
                      <InfoIcon />
                    </span>
                  )}
                </div>
                <div className="type-micro-medium text-grey-50">{subTitle}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {tooltipId && (
        <ReactTooltip
          id={tooltipId}
          content={desc}
          className="w-[280px]! whitespace-normal! leading-4! text-left! text-[12px]!"
          place="top"
          opacity={1}
          noArrow
        />
      )}
      {isOpen && <div style={{ marginLeft: `${level === 1 ? 30 : 25}px` }}>{children}</div>}
    </div>
  )
}

EFormAccordion.propTypes = {
  testId: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  defaultState: PropTypes.bool,
  children: PropTypes.node,
  level: PropTypes.number,
  fieldType: PropTypes.string,
  isRequired: PropTypes.bool,
  arrIdx: PropTypes.number,
  isCreated: PropTypes.bool,
  description: PropTypes.string,
}

export default EFormAccordion
